import { ReactNode } from 'react'

import { DisclosureButton } from '@headlessui/react'
import { IconChevronDownSolid, IconChevronRightSolid, IconFile } from '@smartbear/swagger-portal-component-library'
import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import { NavLink, useNavigate, useOutletContext } from 'react-router'

import { ClampTooltip } from '../ClampTooltip'
import { calculateNestedMargin } from '../helpers'
import { RootOutletContext } from '~/portal/types/portal'

export const SideNavItem = ({
  path,
  title,
  isActive,
  icon,
  level,
  isExpandable,
  open,
  isApi,
  selectedItem,
  setSelectedItem,
  onClick,
  isMobile,
  useMarkdocRenderer = false,

}: {
  path: string,
  title: string,
  isActive?: boolean,
  icon?: ReactNode,
  level: number,
  isExpandable?: boolean,
  open?: boolean,
  isApi?: boolean,
  selectedItem?: string | null,
  setSelectedItem?: (selectedItem: string | null) => void,
  onClick?: (e: any) => void,
  isMobile?: boolean,
  useMarkdocRenderer?: boolean,
}) => {
  const { t } = useTranslation()

  const navigate = useNavigate()

  const navItem =
    <div
      className={clsx(
        'flex w-full wrap-break-word overflow-hidden py-3.25 text-body2 items-center',
        (isActive && !selectedItem) ? 'text-(--sp-color-link) font-semibold' : 'text-(--sp-color-text)',
      )}
    >
      <div className={clsx('flex', useMarkdocRenderer ? 'items-center h-full' : 'flex-col self-start h-5'
      )}>
        <span className={clsx(
          'text-(--sp-color-text-muted)',
          useMarkdocRenderer ? '' : 'mt-[0.4rem]'
        )}>
          {icon || <IconFile />}
        </span>
      </div>
      <ClampTooltip text={title} className="ml-2" />
    </div>

  const { closeSideNavWithoutTransition } = useOutletContext<RootOutletContext>()

  const navClick = (e) => {
    if (setSelectedItem) { setSelectedItem(null) }
    onClick?.(e)
    closeSideNavWithoutTransition?.()
  }

  return (
    <div
      aria-label={t('consumer.sideNavigation.sideNavItem.ariaLabel')}
      data-testid={`consumer-side-navigation-item-${title}`}
      style={{ paddingLeft: calculateNestedMargin(level, isMobile) }}
      className={clsx('sp-sidenav-item', isActive && 'sp-sidenav-item--active', level > 0 && 'pr-3')}
    >
      <div className={clsx(
        'sp-sidenav-item-bg flex items-center w-full hover:bg-(--sp-sidenav-item-hover-bg,var(--sp-color-surface-hover))',
        isActive ? 'bg-(--sp-sidenav-item-active-bg,var(--sp-color-surface-raised))' : 'bg-(--sp-sidenav-item-bg,var(--sp-color-surface))',
        level > 0 ? 'pl-2 rounded rounded-b-1 rounded-t-1' : 'pl-4',
        (open && isApi && isActive) && 'rounded-b-none!'
      )}>
        {isExpandable ?
          (isActive || (!isActive && open)) ?
            <div className="flex w-full">
              <DisclosureButton className="flex items-baseline mt-[0.95rem]">
                <div className="flex w-4 text-(--sp-color-text)">
                  {open ? <IconChevronDownSolid width={8} /> : <IconChevronRightSolid width={8} />}
                </div>
              </DisclosureButton>
              <NavLink to={path} className="w-full pr-3" onClick={navClick} >
                {navItem}
              </NavLink>
            </div> :
            <div className="flex w-full">
              <DisclosureButton className="flex items-baseline mt-[0.95rem]">
                <div className="flex w-4 text-(--sp-color-text)">
                  <IconChevronRightSolid width={8} />
                </div>
              </DisclosureButton>
              <DisclosureButton
                className="flex items-center w-full pr-3 text-left"
                data-testid="disclosure-nav-button"
                onClick={() => {
                  navigate(path)
                  closeSideNavWithoutTransition?.()
                }}
              >
                {navItem}
              </DisclosureButton>
            </div>
          :
          <div className="flex w-full">
            <NavLink to={path} className="w-full pr-3 ml-4" onClick={navClick}>
              {navItem}
            </NavLink>
          </div>
        }
      </div>
    </div>
  )
}